<template>
  <BaseContainer class="overflow-hidden">
    <!-- Header -->
    <header class="flex items-center justify-between mb-6">
      <h2
        :class="
          twJoin(
            'md:text-5xl !leading-[62px]',
            recommendation && !style && '!text-[32px]',
            style && '!md:text-5xl font-bold md:font-normal',
            style ? 'text-2xl' : 'text-[32px]'
          )
        "
      >
        {{ title }}
      </h2>

      <section class="flex gap-x-2">
        <div
          :class="
            twJoin(
              props.slideKey +
                '-c-button-prev w-8 h-8 flex items-center justify-center rounded bg-black text-white cursor-pointer'
            )
          "
        >
          <Icon name="arrow-left" />
        </div>
        <div
          :class="
            twJoin(
              props.slideKey +
                '-c-button-next w-8 h-8 flex items-center justify-center rounded bg-black text-white cursor-pointer'
            )
          "
        >
          <Icon name="arrow-right" />
        </div>
      </section>
    </header>

    <!-- Content -->
    <template v-if="isLoading">
      <nuxt-error-boundary @error="() => {}">
        <Swiper
          :modules="[SwiperEffectCreative, SwiperNavigation]"
          :slides-per-view="width < 768 ? 2 : 'auto'"
          :space-between="recommendation ? 24 : 16"
          :navigation="{
            enabled: true,
            disabledClass: '!bg-gray-10 !text-typo-disabled',
            nextEl: `.${props.slideKey}-c-button-next`,
            prevEl: `.${props.slideKey}-c-button-prev`
          }"
          :creative-effect="{
            prev: {
              shadow: false,
              translate: ['-20%', 0, -1]
            },
            next: {
              translate: ['100%', 0, 0]
            }
          }"
          class="!overflow-visible md:!overflow-clip"
        >
          <SwiperSlide
            v-for="(item, index) in new Array(4).fill(0)"
            :key="index"
            :class="
              twJoin(
                'min-w-[230px] max-w-[240px] md:max-w-[282px]',
                recommendation &&
                  '!max-w-[unset] md:!max-w-[277px] !min-w-[unset] md:!min-w-[230px]'
              )
            "
          >
            <ProductItemThumbnailSkeleton :key="index" product-listing />
          </SwiperSlide>
        </Swiper>
      </nuxt-error-boundary>
    </template>
    <template v-else>
      <nuxt-error-boundary @error="() => {}">
        <Swiper
          :modules="[SwiperEffectCreative, SwiperNavigation]"
          :slides-per-view="width < 768 ? 2 : 'auto'"
          :space-between="recommendation ? 24 : 16"
          :navigation="{
            enabled: true,
            disabledClass: '!bg-gray-10 !text-typo-disabled',
            nextEl: `.${props.slideKey}-c-button-next`,
            prevEl: `.${props.slideKey}-c-button-prev`
          }"
          :creative-effect="{
            prev: {
              shadow: false,
              translate: ['-20%', 0, -1]
            },
            next: {
              translate: ['100%', 0, 0]
            }
          }"
          class="!overflow-visible md:!overflow-clip"
        >
          <SwiperSlide
            v-for="(item, index) in data"
            :key="index"
            :class="
              twJoin(
                'min-w-[230px] max-w-[240px] md:max-w-[282px]',
                recommendation &&
                  '!max-w-[unset] md:!max-w-[277px] !min-w-[unset] md:!min-w-[230px]'
              )
            "
          >
            <NuxtLinkLocale
              class="w-full hover:cursor hover:opacity-50"
              :to="item.href"
            >
              <div class="relative w-full pt-[100%]">
                <div
                  class="absolute top-0 left-0 w-full h-full bg-primary-white rounded-xl"
                >
                  <img
                    :alt="item.titleEn?.toLowerCase() ?? ''"
                    :src="item.imageUrl"
                    class="w-full h-full object-cover border-border-divider rounded-xl"
                  />
                </div>
              </div>
              <div
                class="bg-primary-white relative z-10 pt-4 text-base md:text-xl font-bold"
              >
                {{
                  locale === 'en' ? item.titleEn : item.titleTh || item.titleEn
                }}
              </div>
            </NuxtLinkLocale>
          </SwiperSlide>
        </Swiper>
      </nuxt-error-boundary>
    </template>
  </BaseContainer>
</template>

<script lang="ts" setup>
import { twJoin } from 'tailwind-merge'
const props = defineProps<{
  slideKey: string
  recommendation?: boolean
  style?: boolean
  title: string
  data: {
    headerEn?: string
    headerTh?: string
    imageUrl?: string
    mobileImageUrl?: string
    titleEn: string
    titleTh: string
    descriptionEn: string
    descriptionTh?: string
    href?: string
  }[]
  isLoading?: boolean
}>()
const { width } = useWindowSize()
const { locale } = useI18n()
</script>
